export const pairingCompatibilityAnalyticsFlag = 'pairing_compatibility_analytics';
export const pairingCompatibilityAlgorithmVersion = 'pairing-compatibility-v1';

export const calculatedCompatibilityLevels = ['Poor', 'Neutral', 'Good', 'Excellent'] as const;
export const confidenceLevels = ['Low', 'Moderate', 'High', 'Very High'] as const;

export type CalculatedCompatibility = (typeof calculatedCompatibilityLevels)[number];
export type PairingCompatibilityConfidence = (typeof confidenceLevels)[number];
export type ScoreCompleteness = 'Complete' | 'Result Complete' | 'Partial' | 'Result Only' | 'Special Result';
export type TrendDirection = 'Trending Up' | 'Stable' | 'Trending Down' | 'Insufficient evidence';
export type ReviewSeverity = 'None' | 'Minor' | 'Moderate' | 'Significant' | 'Critical';
export type PairingReviewStatus = 'none' | 'needs_review' | 'deferred' | 'accepted' | 'overridden';
export type PairingRatingSource = 'Captain Set' | 'Data Driven' | 'Captain Confirmed Data' | 'Captain Override';

export type PairingSetScore = {
  us: number | null;
  opponent: number | null;
};

export type PairingLineEvidence = {
  matchId: string;
  matchDate: string;
  seasonId?: string | null;
  positionCode?: string | null;
  lineNumber?: number | null;
  lineType?: string | null;
  opponentName?: string | null;
  winner: 'Us' | 'Opponent' | 'Unknown';
  resultValue?: string | null;
  retired?: boolean | null;
  defaulted?: boolean | null;
  walkover?: boolean | null;
  sets: [PairingSetScore, PairingSetScore, PairingSetScore];
  matchTiebreakWinner?: 'Us' | 'Opponent' | 'Unknown' | null;
  matchTiebreakExactScoreKnown?: boolean | null;
  matchTiebreakUsPoints?: number | null;
  matchTiebreakOpponentPoints?: number | null;
};

export type PairingCalculationConfig = {
  algorithmVersion: string;
  weights: {
    results: number;
    dominance: number;
    adaptability: number;
    pressurePerformance: number;
    closingPerformance: number;
    consistency: number;
    synergy: number;
    lineContext: number;
  };
  thresholds: {
    excellent: number;
    good: number;
    neutral: number;
  };
  evidenceThresholds: {
    minimumMatches: number;
    minimumRegulationSets: number;
    minimumMatchesWithScores: number;
  };
  currentSeasonWeight: number;
  priorSeasonWeight: number;
  olderSeasonWeight: number;
};

export type PairingMetrics = {
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  matchWinPercentage: number | null;
  setsWon: number;
  setsLost: number;
  setWinPercentage: number | null;
  regulationGamesWon: number;
  regulationGamesLost: number;
  regulationGameWinPercentage: number | null;
  totalRegulationGameDifferential: number;
  averageRegulationGameDifferential: number | null;
  straightSetWins: number;
  straightSetLosses: number;
  straightSetWinRate: number | null;
  straightSetLossRate: number | null;
  decidingMatchTiebreakMatches: number;
  decidingMatchTiebreakWins: number;
  decidingMatchTiebreakLosses: number;
  matchTiebreakWinPercentage: number | null;
  exactMatchTiebreakPointsWon: number;
  exactMatchTiebreakPointsLost: number;
  matchTiebreakPointDifferential: number;
  narrowMatchTiebreakWins: number;
  narrowMatchTiebreakLosses: number;
  decisiveMatchTiebreakWins: number;
  decisiveMatchTiebreakLosses: number;
  firstSetComebackOpportunities: number;
  firstSetComebackWins: number;
  comebackWinPercentage: number | null;
  firstSetLossSecondSetWins: number;
  firstSetLossMatchWins: number;
  lopsidedFirstSetRecoveries: number;
  setToSetMarginImprovementTotal: number;
  matchesWonAfterWinningFirstSet: number;
  matchesLostAfterWinningFirstSet: number;
  closingPercentage: number | null;
  lossesAfterWinningFirstSet: number;
  negativeMomentumMatches: number;
  positiveMomentumMatches: number;
  scoreCompletenessSummary: Record<ScoreCompleteness, number>;
  lineBreakdown: Record<string, { matches: number; wins: number; losses: number; averageGameDifferential: number | null; straightSetRate: number | null; matchTiebreakRate: number | null }>;
  currentSeasonRecord: { wins: number; losses: number };
};

export type PairingCalculationResult = {
  algorithmVersion: string;
  sufficientEvidence: boolean;
  compatibility: CalculatedCompatibility | null;
  confidence: PairingCompatibilityConfidence | null;
  numericScore: number | null;
  trendDirection: TrendDirection;
  componentScores: Record<keyof PairingCalculationConfig['weights'], number>;
  metrics: PairingMetrics;
  matchStory: string;
  matchesIncluded: string[];
  evidenceSummary: string;
};

export const defaultPairingCompatibilityConfig: PairingCalculationConfig = {
  algorithmVersion: pairingCompatibilityAlgorithmVersion,
  weights: {
    results: 25,
    dominance: 15,
    adaptability: 15,
    pressurePerformance: 10,
    closingPerformance: 10,
    consistency: 10,
    synergy: 10,
    lineContext: 5,
  },
  thresholds: {
    excellent: 80,
    good: 62,
    neutral: 42,
  },
  evidenceThresholds: {
    minimumMatches: 3,
    minimumRegulationSets: 4,
    minimumMatchesWithScores: 2,
  },
  currentSeasonWeight: 1,
  priorSeasonWeight: 0.7,
  olderSeasonWeight: 0.45,
};

export function normalizePairingIdentity(playerAId: string, playerBId: string): [string, string] {
  if (!playerAId || !playerBId) throw new Error('Both players are required.');
  if (playerAId === playerBId) throw new Error('A pairing requires two different players.');
  return playerAId < playerBId ? [playerAId, playerBId] : [playerBId, playerAId];
}

export function classifyScoreCompleteness(line: PairingLineEvidence): ScoreCompleteness {
  if (line.defaulted || line.walkover || line.retired || ['Default by Opponent', 'Default by Us', 'Double Default', 'Retired Win', 'Retired Loss'].includes(String(line.resultValue || ''))) {
    return 'Special Result';
  }
  if (line.winner === 'Unknown') return 'Result Only';
  const [set1, set2, set3] = line.sets;
  const firstTwoComplete = isCompleteSet(set1) && isCompleteSet(set2);
  if (!firstTwoComplete) {
    const hasAnyScore = line.sets.some(isEnteredSet);
    return hasAnyScore ? 'Partial' : 'Result Only';
  }
  const split = setWinner(set1) !== setWinner(set2);
  if (!split) return 'Complete';
  const exactKnown = Boolean(line.matchTiebreakExactScoreKnown && line.matchTiebreakUsPoints != null && line.matchTiebreakOpponentPoints != null);
  if (exactKnown) return 'Complete';
  if (isMatchTiebreakProxy(set3) || line.matchTiebreakWinner === 'Us' || line.matchTiebreakWinner === 'Opponent') return 'Result Complete';
  if (isEnteredSet(set3)) return 'Complete';
  return 'Partial';
}

export function calculatePairingCompatibility(
  lines: PairingLineEvidence[],
  options: { currentSeasonId?: string | null; config?: PairingCalculationConfig } = {},
): PairingCalculationResult {
  const config = options.config || defaultPairingCompatibilityConfig;
  const qualifying = lines
    .filter((line) => isQualifyingPlayedLine(line))
    .sort((a, b) => a.matchDate.localeCompare(b.matchDate));
  const metrics = emptyMetrics();
  const weightedDiffs: number[] = [];
  const recentResults: number[] = [];

  qualifying.forEach((line) => {
    const completeness = classifyScoreCompleteness(line);
    metrics.scoreCompletenessSummary[completeness] += 1;
    const won = line.winner === 'Us';
    metrics.matchesPlayed += 1;
    if (won) metrics.matchesWon += 1;
    else metrics.matchesLost += 1;
    if (line.seasonId && options.currentSeasonId && line.seasonId === options.currentSeasonId) {
      if (won) metrics.currentSeasonRecord.wins += 1;
      else metrics.currentSeasonRecord.losses += 1;
    }
    recentResults.push(won ? 1 : -1);

    const firstSet = line.sets[0];
    const secondSet = line.sets[1];
    const thirdSet = line.sets[2];
    const regulationSets = [firstSet, secondSet].filter(isCompleteSet);
    regulationSets.forEach((set) => {
      if (setWinner(set) === 'Us') metrics.setsWon += 1;
      else metrics.setsLost += 1;
      metrics.regulationGamesWon += Number(set.us);
      metrics.regulationGamesLost += Number(set.opponent);
    });
    const regulationDiff = regulationSets.reduce((total, set) => total + Number(set.us) - Number(set.opponent), 0);
    metrics.totalRegulationGameDifferential += regulationDiff;
    if (regulationSets.length > 0) weightedDiffs.push(regulationDiff * seasonWeight(line, options.currentSeasonId, config));

    if (isCompleteSet(firstSet) && isCompleteSet(secondSet)) {
      const firstWinner = setWinner(firstSet);
      const secondWinner = setWinner(secondSet);
      if (firstWinner === secondWinner) {
        if (won) metrics.straightSetWins += 1;
        else metrics.straightSetLosses += 1;
      } else {
        metrics.decidingMatchTiebreakMatches += 1;
        if (won) metrics.decidingMatchTiebreakWins += 1;
        else metrics.decidingMatchTiebreakLosses += 1;
        const exact = exactBreaker(line);
        if (exact) {
          metrics.exactMatchTiebreakPointsWon += won ? exact.us : exact.opponent;
          metrics.exactMatchTiebreakPointsLost += won ? exact.opponent : exact.us;
          const margin = Math.abs(exact.us - exact.opponent);
          if (margin <= 2) {
            if (won) metrics.narrowMatchTiebreakWins += 1;
            else metrics.narrowMatchTiebreakLosses += 1;
          } else if (margin >= 5) {
            if (won) metrics.decisiveMatchTiebreakWins += 1;
            else metrics.decisiveMatchTiebreakLosses += 1;
          }
        }
      }

      const firstMargin = Number(firstSet.us) - Number(firstSet.opponent);
      const secondMargin = Number(secondSet.us) - Number(secondSet.opponent);
      metrics.setToSetMarginImprovementTotal += secondMargin - firstMargin;
      if (firstWinner === 'Opponent') {
        metrics.firstSetComebackOpportunities += 1;
        if (secondWinner === 'Us') metrics.firstSetLossSecondSetWins += 1;
        if (won) metrics.firstSetLossMatchWins += 1;
        if (won) metrics.firstSetComebackWins += 1;
        if (Number(firstSet.opponent) - Number(firstSet.us) >= 4 && secondWinner === 'Us') metrics.lopsidedFirstSetRecoveries += 1;
      } else {
        if (won) metrics.matchesWonAfterWinningFirstSet += 1;
        else {
          metrics.matchesLostAfterWinningFirstSet += 1;
          metrics.lossesAfterWinningFirstSet += 1;
        }
      }
      if (secondMargin > firstMargin + 2) metrics.positiveMomentumMatches += 1;
      if (secondMargin < firstMargin - 2) metrics.negativeMomentumMatches += 1;
    }

    const lineKey = String(line.lineNumber || line.positionCode || 'Other');
    metrics.lineBreakdown[lineKey] ||= { matches: 0, wins: 0, losses: 0, averageGameDifferential: null, straightSetRate: null, matchTiebreakRate: null };
    const breakdown = metrics.lineBreakdown[lineKey];
    breakdown.matches += 1;
    if (won) breakdown.wins += 1;
    else breakdown.losses += 1;
  });

  metrics.matchWinPercentage = percentage(metrics.matchesWon, metrics.matchesPlayed);
  metrics.setWinPercentage = percentage(metrics.setsWon, metrics.setsWon + metrics.setsLost);
  metrics.regulationGameWinPercentage = percentage(metrics.regulationGamesWon, metrics.regulationGamesWon + metrics.regulationGamesLost);
  metrics.averageRegulationGameDifferential = metrics.matchesPlayed ? round(metrics.totalRegulationGameDifferential / metrics.matchesPlayed, 2) : null;
  metrics.straightSetWinRate = percentage(metrics.straightSetWins, metrics.matchesPlayed);
  metrics.straightSetLossRate = percentage(metrics.straightSetLosses, metrics.matchesPlayed);
  metrics.matchTiebreakWinPercentage = percentage(metrics.decidingMatchTiebreakWins, metrics.decidingMatchTiebreakMatches);
  metrics.matchTiebreakPointDifferential = metrics.exactMatchTiebreakPointsWon - metrics.exactMatchTiebreakPointsLost;
  metrics.comebackWinPercentage = percentage(metrics.firstSetComebackWins, metrics.firstSetComebackOpportunities);
  metrics.closingPercentage = percentage(metrics.matchesWonAfterWinningFirstSet, metrics.matchesWonAfterWinningFirstSet + metrics.matchesLostAfterWinningFirstSet);

  const matchesWithScores = qualifying.filter((line) => ['Complete', 'Result Complete'].includes(classifyScoreCompleteness(line))).length;
  const sufficientEvidence = metrics.matchesPlayed >= config.evidenceThresholds.minimumMatches
    && (metrics.setsWon + metrics.setsLost) >= config.evidenceThresholds.minimumRegulationSets
    && matchesWithScores >= config.evidenceThresholds.minimumMatchesWithScores;

  const componentScores = scoreComponents(metrics, weightedDiffs);
  const numericScore = sufficientEvidence ? weightedScore(componentScores, config.weights) : null;
  const compatibility = numericScore === null ? null : ratingForScore(numericScore, config);
  const confidence = sufficientEvidence ? confidenceFor(metrics, matchesWithScores) : null;
  const trendDirection = trendFromRecent(recentResults.slice(-3), weightedDiffs.slice(-3));
  return {
    algorithmVersion: config.algorithmVersion,
    sufficientEvidence,
    compatibility,
    confidence,
    numericScore,
    trendDirection,
    componentScores,
    metrics,
    matchStory: buildMatchStory(compatibility, confidence, metrics),
    matchesIncluded: qualifying.map((line) => line.matchId),
    evidenceSummary: sufficientEvidence
      ? `${metrics.matchesPlayed} qualifying matches, ${metrics.setsWon + metrics.setsLost} regulation sets`
      : `${metrics.matchesPlayed} of ${config.evidenceThresholds.minimumMatches} qualifying matches; ${metrics.setsWon + metrics.setsLost} of ${config.evidenceThresholds.minimumRegulationSets} regulation sets`,
  };
}

export function disagreementSeverity(visible?: string | null, calculated?: string | null): ReviewSeverity {
  if (!visible || !calculated || visible === 'Unknown' || calculated === 'Unknown' || visible === calculated) return 'None';
  const order = ['Poor', 'Neutral', 'Good', 'Excellent'];
  const diff = Math.abs(order.indexOf(visible) - order.indexOf(calculated));
  if (diff >= 3) return 'Critical';
  if (diff === 2) return 'Significant';
  if (diff === 1) return visible === 'Neutral' || calculated === 'Neutral' || visible === 'Poor' || calculated === 'Poor' ? 'Moderate' : 'Minor';
  return 'None';
}

function emptyMetrics(): PairingMetrics {
  return {
    matchesPlayed: 0,
    matchesWon: 0,
    matchesLost: 0,
    matchWinPercentage: null,
    setsWon: 0,
    setsLost: 0,
    setWinPercentage: null,
    regulationGamesWon: 0,
    regulationGamesLost: 0,
    regulationGameWinPercentage: null,
    totalRegulationGameDifferential: 0,
    averageRegulationGameDifferential: null,
    straightSetWins: 0,
    straightSetLosses: 0,
    straightSetWinRate: null,
    straightSetLossRate: null,
    decidingMatchTiebreakMatches: 0,
    decidingMatchTiebreakWins: 0,
    decidingMatchTiebreakLosses: 0,
    matchTiebreakWinPercentage: null,
    exactMatchTiebreakPointsWon: 0,
    exactMatchTiebreakPointsLost: 0,
    matchTiebreakPointDifferential: 0,
    narrowMatchTiebreakWins: 0,
    narrowMatchTiebreakLosses: 0,
    decisiveMatchTiebreakWins: 0,
    decisiveMatchTiebreakLosses: 0,
    firstSetComebackOpportunities: 0,
    firstSetComebackWins: 0,
    comebackWinPercentage: null,
    firstSetLossSecondSetWins: 0,
    firstSetLossMatchWins: 0,
    lopsidedFirstSetRecoveries: 0,
    setToSetMarginImprovementTotal: 0,
    matchesWonAfterWinningFirstSet: 0,
    matchesLostAfterWinningFirstSet: 0,
    closingPercentage: null,
    lossesAfterWinningFirstSet: 0,
    negativeMomentumMatches: 0,
    positiveMomentumMatches: 0,
    scoreCompletenessSummary: { Complete: 0, 'Result Complete': 0, Partial: 0, 'Result Only': 0, 'Special Result': 0 },
    lineBreakdown: {},
    currentSeasonRecord: { wins: 0, losses: 0 },
  };
}

function isQualifyingPlayedLine(line: PairingLineEvidence): boolean {
  if (classifyScoreCompleteness(line) === 'Special Result') return false;
  return line.winner === 'Us' || line.winner === 'Opponent';
}

function isEnteredSet(set?: PairingSetScore): boolean {
  return Boolean(set && set.us != null && set.opponent != null);
}

function isCompleteSet(set?: PairingSetScore): set is { us: number; opponent: number } {
  if (!isEnteredSet(set)) return false;
  const us = Number(set?.us);
  const opponent = Number(set?.opponent);
  const high = Math.max(us, opponent);
  const low = Math.min(us, opponent);
  return high >= 6 && (high - low >= 2 || (high === 7 && low === 6));
}

function isMatchTiebreakProxy(set?: PairingSetScore): boolean {
  return Boolean(set && ((set.us === 1 && set.opponent === 0) || (set.us === 0 && set.opponent === 1)));
}

function setWinner(set: { us: number | null; opponent: number | null }): 'Us' | 'Opponent' {
  return Number(set.us || 0) > Number(set.opponent || 0) ? 'Us' : 'Opponent';
}

function exactBreaker(line: PairingLineEvidence): { us: number; opponent: number } | null {
  if (!line.matchTiebreakExactScoreKnown || line.matchTiebreakUsPoints == null || line.matchTiebreakOpponentPoints == null) return null;
  return { us: line.matchTiebreakUsPoints, opponent: line.matchTiebreakOpponentPoints };
}

function percentage(numerator: number, denominator: number): number | null {
  return denominator > 0 ? round((numerator / denominator) * 100, 1) : null;
}

function round(value: number, decimals = 0): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function seasonWeight(line: PairingLineEvidence, currentSeasonId: string | null | undefined, config: PairingCalculationConfig): number {
  if (!currentSeasonId) return config.currentSeasonWeight;
  if (!line.seasonId) return config.priorSeasonWeight;
  return line.seasonId === currentSeasonId ? config.currentSeasonWeight : config.priorSeasonWeight;
}

function bounded(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function scoreComponents(metrics: PairingMetrics, weightedDiffs: number[]): PairingCalculationResult['componentScores'] {
  const avgDiff = metrics.averageRegulationGameDifferential || 0;
  return {
    results: bounded(50 + ((metrics.matchWinPercentage ?? 50) - 50) * 0.9 + ((metrics.setWinPercentage ?? 50) - 50) * 0.35),
    dominance: bounded(50 + avgDiff * 7 + metrics.straightSetWins * 3 - metrics.straightSetLosses * 4),
    adaptability: bounded(45 + metrics.firstSetComebackWins * 12 + metrics.lopsidedFirstSetRecoveries * 8 + metrics.firstSetLossSecondSetWins * 5 + Math.max(0, metrics.setToSetMarginImprovementTotal) * 1.5),
    pressurePerformance: bounded(50 + (metrics.matchTiebreakWinPercentage == null ? 0 : (metrics.matchTiebreakWinPercentage - 50) * 0.7) + metrics.narrowMatchTiebreakWins * 4 - metrics.narrowMatchTiebreakLosses * 4),
    closingPerformance: bounded(55 + (metrics.closingPercentage == null ? 0 : (metrics.closingPercentage - 70) * 0.55) - metrics.lossesAfterWinningFirstSet * 10 - metrics.negativeMomentumMatches * 3),
    consistency: bounded(62 - standardDeviation(weightedDiffs) * 4 + Math.max(0, metrics.matchesWon - metrics.matchesLost) * 2),
    synergy: 50,
    lineContext: bounded(50 + Object.entries(metrics.lineBreakdown).reduce((bonus, [line, breakdown]) => {
      const numericLine = Number(line);
      return bonus + (Number.isFinite(numericLine) && numericLine === 1 && breakdown.wins > breakdown.losses ? 4 : 0);
    }, 0)),
  };
}

function weightedScore(scores: PairingCalculationResult['componentScores'], weights: PairingCalculationConfig['weights']): number {
  const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
  const total = (Object.keys(weights) as Array<keyof PairingCalculationConfig['weights']>).reduce((sum, key) => sum + scores[key] * weights[key], 0);
  return round(total / totalWeight, 1);
}

function ratingForScore(score: number, config: PairingCalculationConfig): CalculatedCompatibility {
  if (score >= config.thresholds.excellent) return 'Excellent';
  if (score >= config.thresholds.good) return 'Good';
  if (score >= config.thresholds.neutral) return 'Neutral';
  return 'Poor';
}

function confidenceFor(metrics: PairingMetrics, matchesWithScores: number): PairingCompatibilityConfidence {
  const completeRatio = metrics.matchesPlayed ? (metrics.scoreCompletenessSummary.Complete + metrics.scoreCompletenessSummary['Result Complete']) / metrics.matchesPlayed : 0;
  const base = metrics.matchesPlayed * 12 + matchesWithScores * 8 + completeRatio * 25 - metrics.scoreCompletenessSummary['Result Only'] * 8 - metrics.scoreCompletenessSummary['Special Result'] * 10;
  if (base >= 115) return 'Very High';
  if (base >= 85) return 'High';
  if (base >= 55) return 'Moderate';
  return 'Low';
}

function trendFromRecent(recentResults: number[], recentDiffs: number[]): TrendDirection {
  if (recentResults.length < 2 && recentDiffs.length < 2) return 'Insufficient evidence';
  const resultTrend = recentResults.reduce((sum, value) => sum + value, 0);
  const diffTrend = recentDiffs.length ? recentDiffs[recentDiffs.length - 1] - recentDiffs[0] : 0;
  if (resultTrend > 0 && diffTrend >= -1) return 'Trending Up';
  if (resultTrend < 0 && diffTrend <= 1) return 'Trending Down';
  return 'Stable';
}

function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.sqrt(values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length);
}

function buildMatchStory(rating: CalculatedCompatibility | null, confidence: PairingCompatibilityConfidence | null, metrics: PairingMetrics): string {
  if (!rating || !confidence) {
    return `No calculated compatibility has been established yet. Current evidence includes ${metrics.matchesPlayed} qualifying match${metrics.matchesPlayed === 1 ? '' : 'es'}; at least three completed played matches are required.`;
  }
  const pieces = [
    `This pairing is rated ${rating} with ${confidence} confidence based on ${metrics.matchesPlayed} completed match${metrics.matchesPlayed === 1 ? '' : 'es'}.`,
    `They are ${metrics.matchesWon}-${metrics.matchesLost} together`,
  ];
  if (metrics.setWinPercentage != null) pieces.push(`have won ${metrics.setWinPercentage}% of completed regulation sets`);
  if (metrics.averageRegulationGameDifferential != null) pieces.push(`average a ${metrics.averageRegulationGameDifferential >= 0 ? '+' : ''}${metrics.averageRegulationGameDifferential} regulation-game differential`);
  let sentence = `${pieces[0]} ${pieces.slice(1).join(', ')}.`;
  if (metrics.firstSetComebackWins > 0) sentence += ` They have ${metrics.firstSetComebackWins} comeback win${metrics.firstSetComebackWins === 1 ? '' : 's'} after losing the first set.`;
  if (metrics.decidingMatchTiebreakMatches > 0) sentence += ` They are ${metrics.decidingMatchTiebreakWins}-${metrics.decidingMatchTiebreakLosses} in deciding match tiebreaks.`;
  if (metrics.lossesAfterWinningFirstSet > 0) sentence += ` ${metrics.lossesAfterWinningFirstSet} loss${metrics.lossesAfterWinningFirstSet === 1 ? '' : 'es'} after winning the first set kept the closing-performance component lower.`;
  if (metrics.scoreCompletenessSummary['Result Complete'] > 0) sentence += ` Some deciding tiebreak winners are known even though exact tiebreak points were not recorded.`;
  return sentence;
}
